import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { Megaphone, BarChart3 } from 'lucide-react';
import { Callout, Stat } from '@/shared/ui';
import { formatConversions } from '@/shared/utils/format';
import type { OverviewFlags, OverviewMarketing } from '../api/overviewApi';
import { LockedSection } from './LockedSection';
import styles from './MarketingSection.module.css';

interface MarketingSectionProps {
  flags: OverviewFlags;
  data: OverviewMarketing;
}

const PLATFORM_LABELS: Record<string, string> = {
  google: 'Google',
  meta: 'Meta',
  tiktok: 'TikTok',
};

export function MarketingSection({ flags, data }: MarketingSectionProps) {
  const navigate = useNavigate();

  if (!flags.hasAds) {
    return (
      <LockedSection
        icon={Megaphone}
        ctaColor="blue"
        headline="Connect your ad accounts to track marketing performance"
        sub="See cumulative spend and conversions across Google Ads and Meta in one place. PracticeNova audits your existing campaigns automatically."
        cta="Connect Google & Meta →"
        onCtaClick={() => navigate('/settings/paid')}
      />
    );
  }

  const isBuilding = flags.isActive && !flags.isOngoing;
  const hasStrategy = data.hasStrategy;
  const platformLabel =
    flags.hasGoogleAds && flags.hasMetaAds
      ? 'Google Ads & Meta'
      : flags.hasMetaAds
        ? 'Meta'
        : 'Google Ads';

  return (
    <div className={styles.root}>
      {/* KPI row */}
      <div className={styles.kpiGrid}>
        <div className={styles.card}>
          <Stat
            label="Total Spend (MTD)"
            value={formatCurrency(data.totalSpend)}
            sub={`${platformLabel} · all campaigns`}
            size="lg"
          />
        </div>
        <div className={styles.card}>
          <Stat
            label="Conversions (MTD)"
            value={formatConversions(data.conversions)}
            sub={`Form fills & calls · as reported by ${platformLabel}`}
            size="lg"
          />
        </div>
        <div className={styles.card}>
          <Stat
            label="Cost per Conversion"
            value={formatCurrency(data.costPerConversion)}
            size="lg"
            sub={isBuilding ? 'Early data — stabilizing' : undefined}
          />
        </div>
      </div>

      {/* Building notice */}
      {isBuilding && (
        <Callout variant="info" icon={BarChart3} title="Results are building.">
          Campaigns launched {data.daysSinceLaunch ?? 12} days ago. Cost per conversion typically
          stabilizes after 30 days of optimization — first performance review available soon.
        </Callout>
      )}

      {/* Campaign table */}
      <div className={styles.tableCard}>
        <div className={styles.tableHeader}>
          <div className={styles.tableTitle}>Campaign Breakdown · {platformLabel}</div>
          {!hasStrategy ? (
            <button
              type="button"
              className={styles.linkAccent}
              onClick={() => navigate('/strategy/builder')}
            >
              Create a strategy to link campaigns to goals →
            </button>
          ) : (
            <button
              type="button"
              className={styles.linkInfo}
              onClick={() => navigate('/campaigns/performance')}
            >
              View full performance →
            </button>
          )}
        </div>

        {data.campaigns.length === 0 ? (
          <div className={styles.empty}>No active campaigns yet.</div>
        ) : (
          <div
            className={clsx(
              styles.table,
              hasStrategy ? styles.tableWithStrategy : styles.tableNoStrategy,
            )}
          >
            <div className={styles.th}>Campaign</div>
            <div className={styles.th}>Platform</div>
            <div className={styles.th}>Status</div>
            <div className={styles.th}>Spend</div>
            <div className={styles.th}>Conv.</div>
            {hasStrategy && <div className={styles.th}>Strategy</div>}

            {data.campaigns.map((c, i) => (
              <div key={i} className={styles.row}>
                <div className={styles.cell}>{c.name}</div>
                <div className={styles.cell}>
                  <span className={styles.platformPill}>
                    {PLATFORM_LABELS[c.platform] ?? c.platform}
                  </span>
                </div>
                <div className={styles.cell}>
                  <span
                    className={clsx(
                      styles.statusPill,
                      c.status === 'Active' ? styles.statusActive : styles.statusPaused,
                    )}
                  >
                    {c.status}
                  </span>
                </div>
                <div className={styles.cell}>{formatCurrency(c.spend)}</div>
                <div className={styles.cell}>{formatConversions(c.conversions)}</div>
                {hasStrategy && (
                  <div className={clsx(styles.cell, styles.cellStrategy)}>
                    {c.strategyName ?? '—'}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className={styles.footnote}>
          Conversions = form fills and calls as reported by {platformLabel}
        </div>
      </div>
    </div>
  );
}

function formatCurrency(n: number | null): string {
  if (n === null) return '—';
  if (n >= 1000) return `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  return `$${n.toFixed(2)}`;
}
